// src/Form.js - Revamped UI with Tabs, Fieldsets, and Animation Fix

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app, db } from "./firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const auth = getAuth(app);
const storage = getStorage(app);

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const ALLOWED_EXTENSIONS = ".pdf, .docx";

// --- Reusable Tab Button Component ---
const TabButton = ({ label, isActive, onClick }) => (
    <button
        type="button"
        className={`tab-button ${isActive ? "active" : ""}`}
        onClick={onClick}
    >
        {label}
    </button>
);

export default function Form() {
  const navigate = useNavigate();

  // --- State Variables ---
  const [fullName, setFullName] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [targetRoles, setTargetRoles] = useState("");
  const [feedbackType, setFeedbackType] = useState([]);
  const [otherFeedback, setOtherFeedback] = useState("");
  const [showOtherInput, setShowOtherInput] = useState(false);

  const [resumeInputMethod, setResumeInputMethod] = useState('link');
  const [coverLetterInputMethod, setCoverLetterInputMethod] = useState('link');

  const [resumeLink, setResumeLink] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileError, setResumeFileError] = useState("");

  const [coverLetterLink, setCoverLetterLink] = useState("");
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const [coverLetterFileError, setCoverLetterFileError] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const formRef = useRef(null);
  const resumeFileInputRef = useRef(null);
  const coverLetterFileInputRef = useRef(null);

  // --- Effects ---
  useEffect(() => {
    // This effect handles the authentication check
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setVerifiedEmail(user.email);
        setUserId(user.uid);
        setIsLoadingAuth(false);
      } else {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // --- CORRECTED: Animation Effect with Safety Check ---
  useEffect(() => {
    //
    // THE FIX: This "guard clause" prevents the "Cannot read properties of null" error
    // by waiting for the form to be rendered and auth to be loaded.
    //
    if (isLoadingAuth || !formRef.current) {
      return; 
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elementsToObserve = formRef.current.querySelectorAll(".reveal");
    elementsToObserve.forEach((el) => {
      observer.observe(el);
    });

    return () => {
        elementsToObserve.forEach((el) => {
            if (el) { // Add a check to ensure element exists before unobserving
                observer.unobserve(el);
            }
        });
    };
  }, [isLoadingAuth]);

  // --- Event Handlers ---
  const validateFile = (file) => {
    if (!file) return null;
    if (!ALLOWED_TYPES.includes(file.type)) return `Invalid file type. Only PDF and DOCX allowed.`;
    if (file.size > MAX_FILE_SIZE_BYTES) return `File exceeds ${MAX_FILE_SIZE_MB}MB limit.`;
    return null;
  };

  const handleResumeFileChange = (e) => {
    const file = e.target.files[0];
    const error = validateFile(file);
    setResumeFileError(error || "");
    setResumeFile(file || null);
  };

  const handleCoverLetterFileChange = (e) => {
    const file = e.target.files[0];
    const error = validateFile(file);
    setCoverLetterFileError(error || "");
    setCoverLetterFile(file || null);
  };
  
  const handleFeedbackChange = (e) => {
    const { value, checked } = e.target;
    if (value === "Other") setShowOtherInput(checked);
    setFeedbackType(prev => checked ? [...prev, value] : prev.filter(item => item !== value));
  };

  // --- Main Submit Logic ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!verifiedEmail || !userId || isSubmitting) return;

    if (resumeFileError || coverLetterFileError) {
      alert("Please fix file errors before submitting.");
      return;
    }

    if (resumeInputMethod === 'link' && !resumeLink) {
        setResumeFileError("A resume link or file is required.");
        return;
    }
    if (resumeInputMethod === 'upload' && !resumeFile) {
        setResumeFileError("A resume link or file is required.");
        return;
    }

    setIsSubmitting(true);

    try {
      let resumeUrl = resumeInputMethod === 'link' ? resumeLink : '';
      let coverLetterUrl = coverLetterInputMethod === 'link' ? coverLetterLink : '';

      if (resumeInputMethod === 'upload' && resumeFile) {
        const resumeRef = ref(storage, `resumes/${userId}/${resumeFile.name}`);
        const snapshot = await uploadBytes(resumeRef, resumeFile);
        resumeUrl = await getDownloadURL(snapshot.ref);
      }

      if (coverLetterInputMethod === 'upload' && coverLetterFile) {
        const coverLetterRef = ref(storage, `coverLetters/${userId}/${coverLetterFile.name}`);
        const snapshot = await uploadBytes(coverLetterRef, coverLetterFile);
        coverLetterUrl = await getDownloadURL(snapshot.ref);
      }

      const feedbackToLog = showOtherInput && otherFeedback
        ? [...feedbackType.filter(item => item !== "Other"), `Other: ${otherFeedback}`]
        : feedbackType;

      const formData = {
        fullName,
        email: verifiedEmail,
        linkedin,
        resume: resumeUrl || "-",
        coverLetter: coverLetterUrl || "-",
        targetRoles,
        feedbackRequested: feedbackToLog,
        timestamp: serverTimestamp(),
      };

      await addDoc(collection(db, "users"), formData);
      navigate(`/confirmation?email=${verifiedEmail}`);

    } catch (error) {
      console.error("Error submitting form or uploading file:", error);
      alert("❌ Submission failed. Check the console for details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingAuth) {
    return <div className="loading-container">Loading...</div>;
  }

  // --- Final JSX with Fieldsets and Tabs ---
  return (
    <div className="form-container apple" ref={formRef}>
      {/* This is the corrected explanation section */}
      <div className="explanation-section reveal">
          <h3 className="explanation-heading">What’s PeerfectCV?</h3>
          <p className="explanation-text">
            PeerfectCV connects you with another jobseeker for honest, peer-to-peer resume feedback.
          </p>
          {/* This paragraph is now restored */}
          <p className="explanation-text">
            Fill out your details and we’ll match you with someone who’s done the same.
          </p>
          {/* This list is now correctly structured */}
          <ul className="benefits-list">
            <li>You give feedback.</li>
            <li>You get feedback.</li>
            <li>You both level up.</li>
          </ul>
      </div>

      <h2 className="form-heading reveal">Let’s Get You Matched!</h2>

      <form className="form-content" onSubmit={handleSubmit}>

        <fieldset className="form-fieldset reveal">
            <legend>Your Details</legend>
            <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
            </div>
            <div className="form-group">
                <label>Email</label>
                <input type="email" value={verifiedEmail || ''} disabled />
            </div>
            <div className="form-group">
                <label htmlFor="linkedin">LinkedIn Profile URL</label>
                <input id="linkedin" type="url" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/your-profile" />
            </div>
        </fieldset>

        <fieldset className="form-fieldset reveal">
            <legend>Application Materials</legend>
            {/* Resume Section */}
            <div className="form-group">
                <label>Resume</label>
                <div className="tab-buttons">
                    <TabButton label="Provide Link" isActive={resumeInputMethod === 'link'} onClick={() => setResumeInputMethod('link')} />
                    <TabButton label="Upload File" isActive={resumeInputMethod === 'upload'} onClick={() => setResumeInputMethod('upload')} />
                </div>
                {resumeInputMethod === 'link' ? (
                     <input type="url" value={resumeLink} onChange={(e) => setResumeLink(e.target.value)} placeholder="https://docs.google.com/document/d/..." required={resumeInputMethod === 'link'} />
                ) : (
                     <input type="file" accept={ALLOWED_EXTENSIONS} onChange={handleResumeFileChange} ref={resumeFileInputRef} required={resumeInputMethod === 'upload'} />
                )}
                {resumeFileError && <p className="message error-message">❌ {resumeFileError}</p>}
            </div>

            {/* Cover Letter Section */}
            <div className="form-group">
                <label>Cover Letter (Optional)</label>
                 <div className="tab-buttons">
                    <TabButton label="Provide Link" isActive={coverLetterInputMethod === 'link'} onClick={() => setCoverLetterInputMethod('link')} />
                    <TabButton label="Upload File" isActive={coverLetterInputMethod === 'upload'} onClick={() => setCoverLetterInputMethod('upload')} />
                </div>
                {coverLetterInputMethod === 'link' ? (
                     <input type="url" value={coverLetterLink} onChange={(e) => setCoverLetterLink(e.target.value)} placeholder="https://docs.google.com/document/d/..." />
                ) : (
                     <input type="file" accept={ALLOWED_EXTENSIONS} onChange={handleCoverLetterFileChange} ref={coverLetterFileInputRef} />
                )}
                {coverLetterFileError && <p className="message error-message">❌ {coverLetterFileError}</p>}
            </div>
        </fieldset>
        
        <fieldset className="form-fieldset reveal">
            <legend>Your Goals</legend>
            <div className="form-group">
                <label htmlFor="targetRoles">What roles are you targeting?</label>
                <input id="targetRoles" type="text" value={targetRoles} onChange={(e) => setTargetRoles(e.target.value)} placeholder="e.g., Product Manager, Software Engineer" />
            </div>
            <div className="form-group">
                <label>What kind of feedback do you want?</label>
                <div className="checkbox-grid">
                    {["Formatting", "Content", "ATS Optimization", "Other"].map((option) => (
                        <label key={option} htmlFor={`feedback-${option}`} className="checkbox-label">
                            <input type="checkbox" id={`feedback-${option}`} value={option} checked={feedbackType.includes(option)} onChange={handleFeedbackChange} />
                            {option}
                        </label>
                    ))}
                </div>
                {showOtherInput && (
                    <input type="text" placeholder="Specify Other Feedback" value={otherFeedback} onChange={(e) => setOtherFeedback(e.target.value)} className="other-feedback-input" />
                )}
            </div>
        </fieldset>

        <button className="form-submit reveal" type="submit" disabled={isSubmitting || !verifiedEmail}>
          {isSubmitting ? "Submitting..." : "Submit for Feedback"}
        </button>
      </form>
    </div>
  );
}